/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// USE FORM SUBMISSION - Standardized form submission hook
// ============================================================================

"use client";

import { isApiFailure, type ApiResult } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

// ============================================================================
// TYPES
// ============================================================================

export interface UseFormSubmissionOptions<TData, TPayload> {
  /**
   * Submit function that returns an ApiResult
   */
  submitFn: (payload: TPayload) => Promise<ApiResult<TData>>;
  /**
   * Success message to display
   */
  successMessage: string;
  /**
   * Error message prefix for API failures
   */
  errorMessagePrefix: string;
  /**
   * Callback when submission succeeds
   */
  onSuccess?: (data: TData) => void;
  /**
   * Callback when submission fails
   */
  onError?: (error: string) => void;
}

export interface UseFormSubmissionReturn<TData, TPayload> {
  /**
   * Whether submission is in progress
   */
  isBusy: boolean;
  /**
   * Current form error message
   */
  formError: string | null;
  /**
   * Submit handler function
   */
  submit: (payload: TPayload) => Promise<TData | null>;
  /**
   * Clear form error
   */
  clearError: () => void;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Standardized hook for form submission with API client integration
 *
 * This hook manages form submission state (isBusy, formError) and handles
 * API response processing, error handling, and toast notifications.
 *
 * @example
 * ```tsx
 * const { isBusy, formError, submit } = useFormSubmission({
 *   submitFn: async (payload) => {
 *     const apiClient = createApiClient({ getToken });
 *     return mode === "create"
 *       ? await apiClient.post("/api/suppliers", payload)
 *       : await apiClient.patch(`/api/suppliers/${id}`, payload);
 *   },
 *   successMessage: mode === "create" ? "Supplier created" : "Supplier updated",
 *   errorMessagePrefix: "Supplier operation failed",
 *   onSuccess: (data) => {
 *     console.log("Success!", data);
 *   },
 * });
 *
 * const handleSubmit = async () => {
 *   const result = await submit(formValues);
 *   if (result) {
 *     // Handle success
 *   }
 * };
 * ```
 */
export function useFormSubmission<TData, TPayload = unknown>({
  submitFn,
  successMessage,
  errorMessagePrefix,
  onSuccess,
  onError,
}: UseFormSubmissionOptions<TData, TPayload>): UseFormSubmissionReturn<
  TData,
  TPayload
> {
  const [isBusy, setIsBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const submit = useCallback(
    async (payload: TPayload): Promise<TData | null> => {
      if (submittingRef.current) return null;
      submittingRef.current = true;
      setFormError(null);
      setIsBusy(true);

      try {
        const response = await submitFn(payload);

        // Handle API failure
        if (isApiFailure(response)) {
          toastFromApiFailure(errorMessagePrefix, response);
          const errorMessage = response.message;
          setFormError(errorMessage);
          onError?.(errorMessage);
          return null;
        }

        // Handle success
        const result = response.data;
        toast.success(successMessage, {
          description: `${successMessage} successfully.`,
        });

        onSuccess?.(result);
        return result;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred";
        setFormError(message);
        onError?.(message);
        return null;
      } finally {
        setIsBusy(false);
        submittingRef.current = false;
      }
    },
    [submitFn, successMessage, errorMessagePrefix, onSuccess, onError],
  );

  const clearError = useCallback(() => {
    setFormError(null);
  }, []);

  return {
    isBusy,
    formError,
    submit,
    clearError,
  };
}
